import { useCallback } from 'react';

/**
 * Custom hook for enhanced scrolling to elements with offset and highlighting.
 *
 * @param {Object} options - Configuration options for the scroll behavior
 * @param {number} [options.offset=80] - Offset in pixels to account for fixed headers
 * @param {boolean} [options.highlight=true] - Whether to highlight the target when scrolled to
 * @param {string} [options.highlightColor='#ffffa0'] - The highlight color (light yellow by default)
 * @param {number} [options.highlightDuration=1500] - Duration of the highlight effect in milliseconds
 * @param {number} [options.fadeOutDuration=300] - Duration of the fade-out transition in milliseconds
 * @param {'smooth'|'auto'} [options.behavior='smooth'] - Scroll behavior
 * @returns {Function} A function that scrolls to the specified element
 *
 * Example usage:
 *
 * function MyComponent() {
 *   const scrollToElement = useSmartScroll({
 *     offset: 100,
 *     highlightColor: '#ffeeaa'
 *   });
 *
 *   const handleClick = () => {
 *     scrollToElement('target-element-id');
 *     // Or with override options for this specific scroll:
 *     // scrollToElement('target-element-id', { highlight: false });
 *   };
 *
 *   return <button onClick={handleClick}>Scroll to Element</button>;
 * }
 */

export function useSmartScroll({
  offset = 80,
  highlight = true,
  highlightColor = '#ffffa0',
  highlightDuration = 1500,
  fadeOutDuration = 300,
  behavior = 'smooth'
} = {}) {
  /**
   * Scrolls to the specified element with enhanced behavior
   *
   * @param {string|HTMLElement} target - The element ID or element reference to scroll to
   * @param {Object} [overrideOptions] - Options that override the default settings for this specific scroll
   * @returns {boolean} True if scroll was successful, false otherwise
   */
  const scrollToElement = useCallback(
    (target, overrideOptions = {}) => {
      const scrollOptions = {
        offset: overrideOptions.offset ?? offset,
        highlight: overrideOptions.highlight ?? highlight,
        highlightColor: overrideOptions.highlightColor ?? highlightColor,
        highlightDuration:
          overrideOptions.highlightDuration ?? highlightDuration,
        fadeOutDuration: overrideOptions.fadeOutDuration ?? fadeOutDuration,
        behavior: overrideOptions.behavior ?? behavior
      };

      // Get the target element (accept both ID string or direct element reference)
      const targetElement =
        typeof target === 'string' ? document.getElementById(target) : target;

      if (!targetElement) {
        console.warn(
          `Target element ${
            typeof target === 'string' ? target : 'provided'
          } not found.`
        );
        return false;
      }

      // Get the element's position relative to the viewport
      const elementPosition = targetElement.getBoundingClientRect().top;

      // Calculate position with offset
      const offsetPosition =
        elementPosition + window.scrollY - scrollOptions.offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: scrollOptions.behavior
      });

      // Apply highlight effect if enabled
      if (scrollOptions.highlight) {
        const originalStyles = {
          backgroundColor: targetElement.style.backgroundColor,
          transition: targetElement.style.transition
        };

        targetElement.style.transition = `background-color ${scrollOptions.fadeOutDuration}ms ease`;
        targetElement.style.backgroundColor = scrollOptions.highlightColor;

        // Remove highlight after the specified duration
        setTimeout(() => {
          targetElement.style.backgroundColor = originalStyles.backgroundColor;

          // Clean up transition after fade-out
          setTimeout(() => {
            targetElement.style.transition = originalStyles.transition;
          }, scrollOptions.fadeOutDuration);
        }, scrollOptions.highlightDuration);
      }

      return true;
    },
    [
      offset,
      highlight,
      highlightColor,
      highlightDuration,
      fadeOutDuration,
      behavior
    ]
  );

  return scrollToElement;
}
